// assembleAvatarSettings — pure helper that turns the user's per-feature picks
// from the `suggestAvatarFeatures` card into the Avatar tab's structured
// "properties" prefill envelope (the `settings` carried by dispatchOpenAIPopup).
//
// `features` is the tool payload's feature list:
//   [{ key, label?, options: [{ value, why?, fields: {...} }] }]
// `picks` is keyed by feature key; each value is EITHER an option index into
// that feature's `options`, OR a free-text fallback `{ custom: string }`.
//
// Each chosen option's `fields` patch is merged: the top-level keys
// (gender/style/category/provider) land on the settings root; everything else
// goes into `properties`. Every `additionalDetails` value (from any feature) is
// collected and joined with "; " so posture + setting concatenate cleanly. A
// `{ custom }` pick maps to the feature's primary field (hair→hairColor,
// eyes→eyeColor, posture/setting→additionalDetails, style→style, gender→gender);
// anything without a clean target falls into additionalDetails. Empty / missing
// values are never emitted (no "undefined" strings).

// Field keys that belong on the settings ROOT rather than inside `properties`.
const TOP_LEVEL_FIELDS = ["gender", "style", "category", "provider"];

// Free-text ({custom}) → primary field per feature key.
const CUSTOM_FIELD_BY_FEATURE = {
  hair: "hairColor",
  eyes: "eyeColor",
  posture: "additionalDetails",
  setting: "additionalDetails",
  style: "style",
  gender: "gender",
};

function cleanString(value) {
  return typeof value === "string" ? value.trim() : "";
}

// Resolve the chosen option's `fields` patch for one feature, given its pick.
// Returns a plain { fieldKey: value } object (possibly empty).
function resolveFields(feature, pick) {
  if (pick == null) return {};

  // Free-text fallback: map to the feature's primary field.
  if (typeof pick === "object" && typeof pick.custom !== "undefined") {
    const text = cleanString(pick.custom);
    if (!text) return {};
    const field = CUSTOM_FIELD_BY_FEATURE[feature.key] || "additionalDetails";
    return { [field]: text };
  }

  // Option index into the feature's options.
  const options = Array.isArray(feature.options) ? feature.options : [];
  const option = options[pick];
  if (!option || typeof option.fields !== "object" || option.fields == null) return {};
  return option.fields;
}

export function assembleAvatarSettings(features, picks) {
  const settings = { mode: "properties", properties: {} };
  const extras = [];
  const featureList = Array.isArray(features) ? features : [];
  const pickMap = picks && typeof picks === "object" ? picks : {};

  featureList.forEach((feature) => {
    if (!feature || !feature.key) return;
    const fields = resolveFields(feature, pickMap[feature.key]);
    Object.keys(fields).forEach((fieldKey) => {
      const value = cleanString(fields[fieldKey]);
      if (!value) return;
      if (fieldKey === "additionalDetails") {
        extras.push(value);
      } else if (TOP_LEVEL_FIELDS.includes(fieldKey)) {
        settings[fieldKey] = value;
      } else {
        settings.properties[fieldKey] = value;
      }
    });
  });

  if (extras.length) {
    settings.properties.additionalDetails = extras.join("; ");
  }

  return settings;
}

export default assembleAvatarSettings;
